import { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import clsx from "clsx";
import { Ban, Check, Loader2 } from "lucide-react";
import { Button } from "components/common/Button";
import { SearchInput } from "components/common/SearchInput";
import { useKeyAction } from "hooks/useKeyAction";
import { useAnchoredPosition } from "hooks/useAnchoredPosition";
import { useAutoScrollToRef } from "hooks/useAutoScrollToRef";
import { ConfirmActionModal } from "../../ConfirmActionModal";
import { NO_LV_POSITION } from "../useLvMatchEditor";
import type { LvMatchPopoverProps } from "./types";

const POPOVER_WIDTH_PX = 360;
const POPOVER_MAX_HEIGHT_PX = 360;

export const LvMatchPopover = ({
  anchorRef,
  editor,
  confirmTitle,
  confirmDescription,
}: LvMatchPopoverProps) => {
  const { t } = useTranslation();
  const popoverRef = useRef<HTMLDivElement>(null);

  const {
    isEditing,
    isConfirmOpen,
    searchQuery,
    filteredPositions,
    pendingPositionNumber,
    isSaving,
    errorMessage,
    canSave,
    isRemoving,
    closeEditor,
    setSearchQuery,
    selectPosition,
    openSaveConfirmation,
    closeSaveConfirmation,
    confirmSave,
  } = editor;

  const popoverPosition = useAnchoredPosition({
    isOpen: isEditing,
    anchorRef,
    compute: (rect) => {
      const spaceBelow = window.innerHeight - rect.bottom;
      const placement = spaceBelow < POPOVER_MAX_HEIGHT_PX ? "top" : "bottom";
      return {
        top:
          placement === "top"
            ? rect.top + window.scrollY - 4
            : rect.bottom + window.scrollY + 4,
        left: Math.max(8, rect.right + window.scrollX - POPOVER_WIDTH_PX),
        placement,
      };
    },
  });

  const { containerRef: positionListRef, targetRef: selectedPositionRef } =
    useAutoScrollToRef<HTMLDivElement, HTMLButtonElement>({
      resetDep:
        isEditing && Boolean(popoverPosition) ? pendingPositionNumber : null,
      options: { block: "center" },
    });

  useKeyAction({
    code: "Escape",
    callback: closeEditor,
    options: { isDisabled: !isEditing || isConfirmOpen },
  });

  useEffect(() => {
    if (!isEditing || isConfirmOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (popoverRef.current?.contains(target)) return;
      if (anchorRef.current?.contains(target)) return;
      closeEditor();
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isEditing, isConfirmOpen, closeEditor, anchorRef]);

  return (
    <>
      {isEditing &&
        popoverPosition &&
        createPortal(
          <div
            ref={popoverRef}
            className="absolute z-50 rounded-md border border-gray-200 bg-white shadow-lg flex flex-col"
            style={{
              top: popoverPosition.top,
              left: popoverPosition.left,
              width: POPOVER_WIDTH_PX,
              maxHeight: POPOVER_MAX_HEIGHT_PX,
              transform:
                popoverPosition.placement === "top"
                  ? "translateY(-100%)"
                  : undefined,
            }}
          >
            <div className="p-2 border-b border-gray-100">
              <SearchInput
                value={searchQuery}
                onChange={setSearchQuery}
                placeholder={t("smartSurveyor.lvMatchEditor.searchPlaceholder")}
                autoFocus
              />
            </div>
            <button
              type="button"
              onClick={() => selectPosition(NO_LV_POSITION)}
              className={clsx(
                "flex w-full shrink-0 items-center gap-2 px-3 py-2 text-left border-b border-gray-100 transition-colors",
                isRemoving
                  ? "bg-red-50 border-l-4 border-l-red-500 pl-2"
                  : "hover:bg-red-50",
              )}
            >
              <span
                className={clsx(
                  "flex h-7 w-7 shrink-0 items-center justify-center rounded-full transition-colors",
                  isRemoving
                    ? "bg-red-100 text-red-600"
                    : "bg-gray-100 text-gray-400",
                )}
              >
                <Ban size={15} />
              </span>
              <div className="min-w-0 flex-1">
                <div
                  className={clsx(
                    "text-sm font-semibold",
                    isRemoving ? "text-red-700" : "text-gray-700",
                  )}
                >
                  {t("smartSurveyor.lvMatchEditor.noPosition")}
                </div>
                <div className="text-xs text-gray-400">
                  {t("smartSurveyor.lvMatchEditor.noPositionHint")}
                </div>
              </div>
              {isRemoving && (
                <Check size={16} className="shrink-0 text-red-600" />
              )}
            </button>
            <div ref={positionListRef} className="flex-1 overflow-y-auto">
              {filteredPositions.length === 0 ? (
                <div className="px-3 py-3 text-xs text-gray-500 text-center">
                  {t("smartSurveyor.lvMatchEditor.noResults")}
                </div>
              ) : (
                filteredPositions.map((position) => {
                  const isSelected =
                    pendingPositionNumber === position.position_number;
                  return (
                    <button
                      key={`${position.file}-${position.position_number}`}
                      ref={isSelected ? selectedPositionRef : undefined}
                      type="button"
                      onClick={() => selectPosition(position.position_number)}
                      className={clsx(
                        "flex w-full items-center gap-2 px-3 py-2 text-left border-b border-gray-50 last:border-b-0",
                        isSelected
                          ? "bg-blue-100 border-l-4 border-l-blue-600 pl-2"
                          : "hover:bg-blue-50",
                      )}
                      title={position.description}
                    >
                      <div className="min-w-0 flex-1">
                        <div
                          className={clsx(
                            "text-sm font-semibold",
                            isSelected ? "text-blue-700" : "text-gray-900",
                          )}
                        >
                          {position.position_number}
                        </div>
                        <div
                          className={clsx(
                            "text-xs truncate",
                            isSelected ? "text-blue-900/70" : "text-gray-500",
                          )}
                        >
                          {position.description}
                        </div>
                      </div>
                      {isSelected && (
                        <Check size={16} className="shrink-0 text-blue-600" />
                      )}
                    </button>
                  );
                })
              )}
            </div>
            {errorMessage && (
              <div className="px-3 py-1.5 text-xs text-red-600 border-t border-gray-100">
                {errorMessage}
              </div>
            )}
            <div className="flex items-center justify-end gap-2 px-2 py-2 border-t border-gray-100">
              <Button
                variant="outline"
                size="sm"
                onClick={closeEditor}
                disabled={isSaving}
              >
                {t("common.cancel")}
              </Button>
              <Button
                variant="primary"
                size="sm"
                onClick={openSaveConfirmation}
                disabled={!canSave}
                startAdornment={
                  isSaving ? (
                    <Loader2 className="animate-spin" size={14} />
                  ) : undefined
                }
              >
                {t("common.save")}
              </Button>
            </div>
          </div>,
          document.body,
        )}
      <ConfirmActionModal
        isOpen={isConfirmOpen}
        onClose={closeSaveConfirmation}
        onConfirm={confirmSave}
        action={isRemoving ? "delete" : "approve"}
        title={
          confirmTitle ??
          t(
            isRemoving
              ? "smartSurveyor.lvMatchEditor.confirmModal.removeTitle"
              : "smartSurveyor.lvMatchEditor.confirmModal.title",
          )
        }
        description={
          confirmDescription ??
          t(
            isRemoving
              ? "smartSurveyor.lvMatchEditor.confirmModal.removeDescription"
              : "smartSurveyor.lvMatchEditor.confirmModal.description",
          )
        }
      />
    </>
  );
};
